import { SearchFilter } from '@components/Filters/SearchFilter';
import { LoadingIndicator } from '@components/LoadingIndicator';
import useService from '@modules/ServiceModule/useService';
import {
  Form,
  Stack,
  Table,
  TableColumn,
  TableNoRecords,
} from '@servicetitan/design-system';
import { useMemo, useState } from 'react';
import { orderBy, SortDescriptor } from '@progress/kendo-data-query';
import formatEstimatedWaitTime from '@pages/monitoring/QueuesPage/utils/formatEstimatedWaitTime';
import ErrorComponent from '@components/ErrorComponent';
import { OutboundCallDetails } from '../OutboundCallModal';

interface IOutboundModalQueuesProps {
  setCallDetails: (details: OutboundCallDetails) => void;
}

const OutboundModalQueues: React.FC<IOutboundModalQueuesProps> = ({
  setCallDetails,
}) => {
  const [queueSearchTerm, setQueueSearchTerm] = useState('');
  const [selectedQueue, setSelectedQueue] = useState('');

  const initialSort: Array<SortDescriptor> = [
    { field: 'estimatedWait', dir: 'asc' },
  ];

  const { queues, loading, users } = useService();
  const [sort, setSort] = useState(initialSort);

  const filteredQueuesData = useMemo(() => {
    const filteredData = queues.filter((queue) => {
      return (
        queueSearchTerm.length == 0 ||
        queue.name.toLowerCase().includes(queueSearchTerm.toLowerCase())
      );
    });
    return orderBy(filteredData, sort);
  }, [queueSearchTerm, queues, sort]);

  const pageSize = 4;
  const [page, setPage] = useState({
    skip: 0,
    take: pageSize,
  });

  const processedQueuesData = useMemo(() => {
    return filteredQueuesData
      .slice(page.skip, page.skip + page.take)
      .map((queue) => {
        const activeUsersCount = queue.memberIds.filter((memberId) => {
          return (
            users.find((user) => user.contactCenterUserId === memberId)
              ?.capacity === 0
          );
        }).length;

        return {
          ...queue,
          activeUsersCount, // Injecting the count of active users only
        };
      });
  }, [filteredQueuesData, page.skip, page.take, users]);

  const handlePageChange = (e: { page: { take: number; skip: number } }) => {
    if (page.take !== e.page.take) {
      setPage({ skip: 0, take: e.page.take });
    } else {
      setPage({
        skip: e.page.skip,
        take: e.page.take,
      });
    }
  };

  return (
    <Stack direction='column' spacing={2}>
      <LoadingIndicator show={loading} title='Loading...' />
      <Stack.Item>
        <SearchFilter
          placeholder='Search for a queue'
          className='w-100'
          value={queueSearchTerm}
          onChange={setQueueSearchTerm}
        />
      </Stack.Item>
      <Stack.Item>
        <Table
          borders={{ vertical: false, horizontal: true }}
          loading={loading}
          data={processedQueuesData}
          sortable={true}
          sort={sort}
          onSortChange={(e) => {
            setSort(e.sort);
          }}
          pageable
          total={filteredQueuesData.length}
          skip={page.skip}
          pageSize={page.take}
          striped={false}
          onPageChange={handlePageChange}
        >
          <TableNoRecords>
            <div className='ta-center m-x-auto p-y-6' style={{ width: 420 }}>
              <ErrorComponent
                image='block'
                title='No results found'
                body='Try another search.'
              />
            </div>
          </TableNoRecords>
          <TableColumn
            width={40}
            cell={(props) => (
              <td>
                <Form.Radio
                  checked={selectedQueue === props.dataItem.id}
                  label=''
                  onChange={() => {
                    setSelectedQueue(props.dataItem.id);
                    setCallDetails({
                      number: props.dataItem.entrypoints?.[0] ?? '',
                      callDisplayName: props.dataItem.name,
                    });
                  }}
                />
              </td>
            )}
          />
          <TableColumn field='name' title='Queue' />
          <TableColumn title='Employee' field='activeUsersCount' />
          <TableColumn
            field='status.estimatedWait'
            title='Est. Wait Time'
            cell={(props) => (
              <td>
                {formatEstimatedWaitTime(props.dataItem.status.estimatedWait)}
              </td>
            )}
          />
        </Table>
      </Stack.Item>
    </Stack>
  );
};

export default OutboundModalQueues;
